import React, { useState } from "react";
import { Button, Card, Col, Row, Nav, Modal, InputGroup, Form } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useGoal } from '../hooks/useGoal';
import { GoalForm } from '../components/Goal/GoalForm';
import { GoalStatsCards } from '../components/Goal/GoalStatsCards';
import { GoalTable } from '../components/Goal/GoalTable';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactApexChart from "react-apexcharts";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { GoalHistory } from '../components/Goal/GoalHistory';
import { AIGoalsOptimization } from '../components/Goal/AIGoalsOptimization';
import { ChartWrapper } from '../components/charts/ChartWrapper';
import { NoDataMessage } from '../components/common/NoDataMessage';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Goal() {
    const [showModal, setShowModal] = useState(false);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [progressAmount, setProgressAmount] = useState('');
    const [filter, setFilter] = useState('all');
    const {
        goals,
        loading,
        stats,
        history,
        addGoal,
        updateGoal,
        deleteGoal,
        updateProgress
    } = useGoal();

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedGoal(null);
    };

    const handleCloseProgressModal = () => {
        setShowProgressModal(false);
        setSelectedGoal(null);
        setProgressAmount('');
    };

    const handleSubmit = async (values, formikHelpers) => {
        const submitFn = selectedGoal ? updateGoal : addGoal;
        const result = await submitFn(values, formikHelpers);
        
        if (result.success) {
            toast.success(result.message);
            handleCloseModal();
        } else {
            toast.error(result.message);
        }
    };

    const handleProgressUpdate = async () => {
        if (!progressAmount || isNaN(progressAmount) || progressAmount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        const result = await updateProgress(selectedGoal.id, parseFloat(progressAmount));
        if (result.success) {
            toast.success(result.message);
            handleCloseProgressModal();
        } else {
            toast.error(result.message);
        }
    };

    const filteredGoals = goals.filter(goal => {
        if (filter === 'all') return true;
        return goal.status === filter;
    });

    // Prepare data for category donut chart
    const getCategoryData = () => {
        const categoryTotals = goals.reduce((acc, goal) => {
            acc[goal.category] = (acc[goal.category] || 0) + Number(goal.targetAmount);
            return acc;
        }, {});

        const hasData = Object.keys(categoryTotals).length > 0;

        return {
            data: {
                labels: hasData ? Object.keys(categoryTotals) : ['No Data'],
                datasets: [{
                    data: hasData ? Object.values(categoryTotals) : [1],
                    backgroundColor: hasData ? 
                        ['#506fd9', '#85b6ff', '#33d685', '#0dcaf0', '#1c96e9', '#6e7985'] : 
                        ['#e9ecef'],
                    borderWidth: 1
                }]
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '70%',
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                showAlways: true,
                                label: hasData ? 'Total' : 'No Data',
                                formatter: function (w) {
                                    return hasData ? `$${w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2)}` : 'No Data';
                                }
                            }
                        }
                    }
                }
            },
            options: {
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: {
                            usePointStyle: true,
                            padding: 20
                        }
                    }
                },
                cutout: '70%'
            }
            
        };
    };

    // Prepare data for goal progress overview
    const getGoalData = () => {
        const total = goals.length;
        const achieved = goals.filter(g => (g.currentAmount / g.targetAmount) * 100 >= 100).length;
        const inProgress = goals.filter(g => {
            const progress = (g.currentAmount / g.targetAmount) * 100;
            return progress > 0 && progress < 100;
        }).length;
        const upcoming = goals.filter(g => g.currentAmount === 0).length;

        const hasData = total > 0;

        return {
            series: {
                labels: ['Achieved', 'In Progress', 'Upcoming'],
                datasets: [{
                    data: hasData ? [achieved, inProgress, upcoming] : [1],
                    backgroundColor: hasData ? 
                        ['#33d685', '#506fd9', '#0dcaf0'] : 
                        ['#e9ecef'],
                    borderWidth: 1
                }]
            },
            options: {
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: {
                            usePointStyle: true,
                            padding: 20
                        }
                    }
                },
                cutout: '70%'
            }
        };
    };

    const renderChart = (title, data, options, icon = "ri-pie-chart-line") => (
        <Card className="card-one">
            <Card.Header>
                <Card.Title as="h6">{title}</Card.Title>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : goals.length === 0 ? (
                    <NoDataMessage 
                        icon={icon}
                        message="No goal data available"
                    />
                ) : (
                    <Doughnut 
                        data={data}
                        options={{
                            ...options,
                            maintainAspectRatio: false,
                            responsive: true
                        }}
                    />
                )}
            </Card.Body>
        </Card>
    );

    const renderGoalChart = () => (
        <Card className="card-one">
            <Card.Header>
                <Card.Title as="h6">Goal Progress</Card.Title>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
                <ChartWrapper
                    type="donut"
                    data={getGoalData().series}
                    options={getGoalData().options}
                    noDataIcon="ri-pie-chart-line"
                    noDataMessage="No goal data available"
                />
            </Card.Body>
        </Card>
    );

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Financial Goals</h4>
                    </div>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                    >
                        Add New Goal
                    </Button>
                </div>

                <GoalStatsCards stats={stats} />

                <Card className="card-one mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <Nav className="nav-pills">
                                <Nav.Link
                                    active={filter === 'all'}
                                    onClick={() => setFilter('all')}
                                >All</Nav.Link>
                                <Nav.Link
                                    active={filter === 'in-progress'}
                                    onClick={() => setFilter('in-progress')}
                                >In Progress</Nav.Link>
                                <Nav.Link
                                    active={filter === 'achieved'}
                                    onClick={() => setFilter('achieved')}
                                >Achieved</Nav.Link>
                                <Nav.Link
                                    active={filter === 'upcoming'}
                                    onClick={() => setFilter('upcoming')}
                                >Upcoming</Nav.Link>
                            </Nav>
                        </div>

                        <GoalTable 
                            goals={filteredGoals}
                            onDelete={deleteGoal}
                            onEdit={(goal) => {
                                setSelectedGoal(goal);
                                setShowModal(true);
                            }}
                            onUpdateProgress={(goalId) => {
                                const goal = goals.find(g => g.id === goalId);
                                setSelectedGoal(goal);
                                setShowProgressModal(true);
                            }}
                        />
                    </Card.Body>
                </Card>

                <Row className="g-3">
                    <Col md={6}>
                        {renderChart(
                            'Goals by Category',
                            getCategoryData().data,
                            getCategoryData().options,
                            "ri-pie-chart-line"
                        )}
                    </Col>
                    <Col md={6}>
                        {renderChart(
                            'Goals Progress Overview',
                            getGoalData().series,
                            getGoalData().options,
                            "ri-bar-chart-line"
                        )}
                    </Col>
                </Row>

                <Row className="g-3 mt-4">
                    <Col md={8}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title as="h6">Goal History</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <GoalHistory history={history} />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <AIGoalsOptimization />
                    </Col>
                </Row>

                {/* Goal Form Modal */}
                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedGoal ? 'Edit Goal' : 'Add New Goal'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <GoalForm
                            initialValues={selectedGoal}
                            onSubmit={handleSubmit}
                            onCancel={handleCloseModal}
                        />
                    </Modal.Body>
                </Modal>

                {/* Progress Update Modal */}
                <Modal show={showProgressModal} onHide={handleCloseProgressModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Update Progress</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Current Progress: ${selectedGoal?.currentAmount || 0} of ${selectedGoal?.targetAmount}</p>
                        <Form.Group className="mb-3">
                            <Form.Label>Add Amount</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={progressAmount}
                                    onChange={(e) => setProgressAmount(e.target.value)}
                                    placeholder="Enter amount"
                                />
                            </InputGroup>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseProgressModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleProgressUpdate}>
                            Update Progress
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>
        </React.Fragment>
    );
} 